import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  Box,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  ContentCopy,
  Share
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { TravellerAccessService } from '../../services/travellerAccessService';
import { useToast } from '../../hooks/use-toast';

interface GenerateAccessTokenDialogProps {
  open: boolean;
  onClose: () => void;
  tripId: string;
  travellerId: string;
  travellerName: string;
}

const GenerateAccessTokenDialog: React.FC<GenerateAccessTokenDialogProps> = ({
  open,
  onClose,
  tripId,
  travellerId,
  travellerName
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(false);
  const [shareableUrl, setShareableUrl] = useState<string | null>(null);
  const [hasExistingToken, setHasExistingToken] = useState(false);

  const checkForExistingToken = useCallback(async () => {
    setCheckingExisting(true);
    try {
      const result = await TravellerAccessService.getExistingAccessToken(tripId, travellerId);
      if (result.hasToken && result.token) {
        const url = TravellerAccessService.generateShareableUrl(result.token);
        setShareableUrl(url);
        setHasExistingToken(true);
      } else {
        setHasExistingToken(false);
        setShareableUrl(null);
      }
    } catch (error) {
      console.error('Error checking for existing token:', error);
      // If checking fails, assume no existing token
      setHasExistingToken(false);
      setShareableUrl(null);
    } finally {
      setCheckingExisting(false);
    }
  }, [tripId, travellerId]);

  // Check for existing token when dialog opens
  useEffect(() => {
    if (open && tripId && travellerId) {
      checkForExistingToken();
    }
  }, [open, tripId, travellerId, checkForExistingToken]);

  const handleGenerateToken = async () => {
    setLoading(true);
    try {
      const newToken = await TravellerAccessService.generateAccessToken(tripId, travellerId);
      const url = TravellerAccessService.generateShareableUrl(newToken);
      setShareableUrl(url);
      setHasExistingToken(true);
      toast({
        title: t('traveller.accessToken.generated'),
        description: t('traveller.accessToken.generatedDescription', { name: travellerName }),
        variant: 'success'
      });
    } catch (error) {
      console.error('Error generating token:', error);
      toast({
        title: t('traveller.accessToken.error'),
        description: t('traveller.accessToken.errorDescription'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUrl = async () => {
    if (shareableUrl) {
      try {
        await navigator.clipboard.writeText(shareableUrl);
        toast({
          title: t('traveller.accessToken.copied'),
          description: t('traveller.accessToken.copiedDescription'),
          variant: 'success'
        });
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        toast({
          title: t('traveller.accessToken.copyError'),
          description: t('traveller.accessToken.copyErrorDescription'),
          variant: 'destructive'
        });
      }
    }
  };

  const handleShare = async () => {
    if (shareableUrl && navigator.share) {
      try {
        await navigator.share({
          title: t('traveller.accessToken.shareTitle', { name: travellerName }),
          text: t('traveller.accessToken.shareText', { name: travellerName }),
          url: shareableUrl,
        });
      } catch (error) {
        console.error('Error sharing:', error);
        // Fallback to copy
        handleCopyUrl();
      }
    } else {
      // Fallback to copy
      handleCopyUrl();
    }
  };

  const handleClose = () => {
    setShareableUrl(null);
    setHasExistingToken(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('traveller.accessToken.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            {t('traveller.accessToken.description', { name: travellerName })}
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('traveller.accessToken.securityNote')}
          </Alert>
        </Box>

        {checkingExisting && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {t('traveller.accessToken.checkingExisting', 'Prüfe vorhandenen Link...')}
            </Typography>
          </Box>
        )}

        {shareableUrl && !checkingExisting && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              {hasExistingToken 
                ? t('traveller.accessToken.existingLink', 'Vorhandener persönlicher Link:')
                : t('traveller.accessToken.shareableUrl')
              }
            </Typography>
            <TextField
              fullWidth
              multiline
              value={shareableUrl}
              variant="outlined"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title={t('traveller.accessToken.copyUrl')}>
                      <IconButton onClick={handleCopyUrl} size="small">
                        <ContentCopy />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('traveller.accessToken.share')}>
                      <IconButton onClick={handleShare} size="small">
                        <Share />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ),
              }}
              sx={{ mb: 2 }}
            />
            
            {hasExistingToken && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {t('traveller.accessToken.existingTokenInfo', 'Ein gültiger Link wurde bereits für diesen Teilnehmer erstellt. Sie können den gleichen Link erneut verwenden.')}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleClose}>
          {t('common.close')}
        </Button>
        {!hasExistingToken && !checkingExisting && (
          <Button
            onClick={handleGenerateToken}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading 
              ? t('traveller.accessToken.generating') 
              : t('traveller.accessToken.generate')
            }
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GenerateAccessTokenDialog;
